/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import getInvoices, {
	getInvoicesParams,
	getInvoicesResponse,
} from 'api/invoice/getInvoices'
import getSingleInvoice, {
	getSingleInvoiceResponse,
} from 'api/invoice/getSingleInvoice'

import { Invoice, Payment } from 'types'

/**
 * Types for the slice of Invoice in the store
 */
export interface InvoiceState {
	records: Invoice[]
	totalCount: number
	totalPages: number
	currentPage: number
	loading: boolean
	invoice: Invoice | null
	payments: Payment[] | []
}

/**
 * Initial state of the slice Invoice
 */
const initialState: InvoiceState = {
	records: [],
	totalCount: 0,
	totalPages: 0,
	currentPage: 1,
	loading: false,
	invoice: null,
	payments: [],
}

/**
 * Action for getting Invoice
 *
 * Tries to get Invoice from the API and returns the result in the payload
 *
 * @param {getInvoicesParams} options - Options for the API call
 * @returns {Promise<getInvoicesResponse>} - Response object returned from the server
 * @throws {Error} - Throws an error if the API call fails
 */

export const getInvoicesAction = createAsyncThunk<
	getInvoicesResponse,
	getInvoicesParams
>('invoice/getInvoicesAction', async (options, { rejectWithValue }) => {
	try {
		const response = await getInvoices(options)
		return response
	} catch (error: any) {
		return rejectWithValue(error.message)
	}
})

export const getSingleInvoicesAction = createAsyncThunk<
	getSingleInvoiceResponse,
	string
>('singleInvoice/getSingleInvoicesAction', async (id, { rejectWithValue }) => {
	try {
		const response = await getSingleInvoice(id)
		return response
	} catch (error: any) {
		return rejectWithValue(error.message)
	}
})

export const invoiceSlice = createSlice({
	name: 'invoice',
	initialState,
	reducers: {
		clearInvoice: () => {
			return initialState
		},
	},
	extraReducers: builder => {
		builder.addCase(getInvoicesAction.pending, state => {
			state.loading = true
		})
		builder.addCase(getInvoicesAction.fulfilled, (state, action) => {
			const { records, totalCount, totalPages, currentPage } =
				action.payload.response
			state.records = records
			state.totalCount = totalCount
			state.totalPages = totalPages
			state.currentPage = currentPage
			state.loading = false
		})
		builder.addCase(getInvoicesAction.rejected, state => {
			state.loading = false
		})
		builder.addCase(getSingleInvoicesAction.pending, state => {
			state.loading = true
		})
		builder.addCase(getSingleInvoicesAction.fulfilled, (state, action) => {
			state.invoice = action.payload.invoice
			state.payments = action.payload.payments
			state.loading = false
		})
		builder.addCase(getSingleInvoicesAction.rejected, state => {
			state.loading = false
		})
	},
})

export const { clearInvoice } = invoiceSlice.actions

export default invoiceSlice.reducer
